import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Button, Select, DatePicker, InputNumber, Checkbox, Space, Typography, Alert, message } from 'antd';
import { CheckCircleOutlined, BoxPlotOutlined, ExclamationCircleOutlined, DeleteOutlined } from '@ant-design/icons';
import moment from 'moment';

const { Option } = Select;
const { Text } = Typography;
const { TextArea } = Input;

const PACKAGING_CONDITION_OPTIONS = [
  { value: 'Good', label: 'Good' },
  { value: 'Average', label: 'Average' },
  { value: 'Poor', label: 'Poor' }
];

const LOCATION_TO_STORE_OPTIONS = [
  { value: 'Msr Ashram Store', label: 'Msr Ashram Store' },
  { value: 'Customer Lounge', label: 'Customer Lounge' },
  { value: 'Courier Area', label: 'Courier Area' },
  { value: 'RTO Inventory', label: 'RTO Inventory' }
];

const PACKAGE_TYPE_OPTIONS = [
  { value: 'Pouch', label: 'Pouch' },
  { value: 'Box', label: 'Box' },
  { value: 'Jar', label: 'Jar' }
];

const DAMAGE_TYPE_OPTIONS = [
  { value: 'Broken', label: 'Broken' },
  { value: 'Torn', label: 'Torn' },
  { value: 'Rat Bite', label: 'Rat Bite' },
  { value: 'Expired', label: 'Expired' },
  { value: 'Water Damage', label: 'Water Damage' },
  { value: 'Manufacturing Issue', label: 'Manufacturing Issue' },
  { value: 'Other', label: 'Other' }
];

const DAMAGE_SEVERITY_OPTIONS = [
  { value: 'Low', label: 'Low' },
  { value: 'Medium', label: 'Medium' },
  { value: 'High', label: 'High' }
];

const SCRAP_REASON_OPTIONS = [
  { value: 'Expired', label: 'Expired' },
  { value: 'Contaminated', label: 'Contaminated' },
  { value: 'Beyond Repair', label: 'Beyond Repair' },
  { value: 'Unsafe Product', label: 'Unsafe Product' },
  { value: 'Other', label: 'Other' }
];

const DISPOSAL_METHOD_OPTIONS = [
  { value: 'Destroy', label: 'Destroy' },
  { value: 'Recycle', label: 'Recycle' },
  { value: 'Vendor Return', label: 'Vendor Return' },
  { value: 'Pending Approval', label: 'Pending Approval' }
];

// Basket types that are BLOCKED when physicalReceived is "No"
const BLOCKED_BASKETS_FOR_NO = ['good', 'repacking'];

const InspectionFormModal = ({ visible, onCancel, onSave, product, basketType, darkTheme }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const physicalReceived = Form.useWatch('physicalReceived', form);

  useEffect(() => {
    if (visible && product) {
      form.setFieldsValue({
        physicalReceived: 'Yes',
        quantityReceived: product.quantity,
        expiryDate: product.expiryDate ? moment(product.expiryDate) : null,
        batchNumber: undefined,
        packagingCondition: undefined,
        locationToStore: undefined,
        remarks: undefined,
        packageType: undefined,
        needLabelChange: false,
        needBarcodeChange: false,
        repackingNotes: undefined,
        damageType: undefined,
        damageSeverity: undefined,
        damageNotes: undefined,
        damageImages: [],
        scrapReason: undefined,
        disposalMethod: undefined,
        scrapNotes: undefined,
      });
    }
  }, [visible, product, form]);

  const isPhysicalNo = physicalReceived === 'No';
  const isBlockedBasket = BLOCKED_BASKETS_FOR_NO.includes(basketType);

  const handleOk = async () => {
    // Block good/repacking if physicalReceived is "No"
    if (isPhysicalNo && isBlockedBasket) {
      message.error(`Cannot move to "${basketType}" basket when product was not physically received. Please select "Damaged" or "Scrap" instead.`);
      return;
    }

    setLoading(true);
    try {
      const values = await form.validateFields();
      onSave(product, basketType, values);
      message.success(`Product "${product.name}" moved to ${basketType} basket.`);
      form.resetFields();
      onCancel();
    } catch (errorInfo) {
      message.error('Please fill in all required fields.');
      console.log('Failed:', errorInfo);
    } finally {
      setLoading(false);
    }
  };

  const getModalTitle = () => {
    switch (basketType) {
      case 'good': return <Space><CheckCircleOutlined style={{ color: '#52c41a' }} /> Good Quality Inspection</Space>;
      case 'repacking': return <Space><BoxPlotOutlined style={{ color: '#faad14' }} /> Repacking Required</Space>;
      case 'damaged': return <Space><ExclamationCircleOutlined style={{ color: '#f5222d' }} /> Damaged Product Inspection</Space>;
      case 'scrap': return <Space><DeleteOutlined style={{ color: '#475569' }} /> Scrap Product Inspection</Space>;
      default: return 'Product Inspection';
    }
  };

  const getBasketColor = () => {
    switch (basketType) {
      case 'good': return '#52c41a';
      case 'repacking': return '#faad14';
      case 'damaged': return '#f5222d';
      case 'scrap': return '#475569';
      default: return '#1890ff';
    }
  };

  return (
    <Modal
      title={getModalTitle()}
      open={visible}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel} disabled={loading}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={handleOk}
          loading={loading}
          disabled={isPhysicalNo && isBlockedBasket}
          style={{ backgroundColor: getBasketColor(), borderColor: getBasketColor() }}
        >
          Save & Classify
        </Button>,
      ]}
      width={760}
      centered
      className={`inspection-modal ${darkTheme ? 'dark-theme' : ''}`}
    >
      {product && (
        <div style={{ marginBottom: 20, padding: 15, borderRadius: 8, backgroundColor: darkTheme ? '#262626' : '#f7f8fa', border: `1px solid ${darkTheme ? '#434343' : '#e8e8e8'}` }}>
          <Text strong style={{ color: darkTheme ? '#ffffff' : '#262626' }}>Product: {product.name}</Text>
          <Text type="secondary" style={{ display: 'block' }}>SKU: {product.sku} | Qty: {product.quantity}</Text>
        </div>
      )}

      {/* Warning when No is selected and basket is blocked */}
      {isPhysicalNo && isBlockedBasket && (
        <Alert
          type="error"
          showIcon
          message={`Cannot classify as "${basketType === 'good' ? 'Good (Restock)' : 'Repacking'}"`}
          description="Product was not physically received. It can only be classified as Damaged or Scrap."
          style={{ marginBottom: 16 }}
        />
      )}

      <Form form={form} layout="vertical" name="inspection_form">
        <div className="inspection-form-grid">
          <Form.Item name="physicalReceived" label="Physical Product Received" rules={[{ required: true, message: 'Please select status!' }]}>
            <Select>
              <Option value="Yes">Yes</Option>
              <Option value="No">No</Option>
            </Select>
          </Form.Item>

<Form.Item
          name="quantityReceived"
          label="Quantity Received"
          rules={[
            { required: true, message: 'Please enter quantity!' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                const qty = Number(value);
                const maxQty = Number(product?.quantity || 0);
                if (!qty || qty < 1) {
                  return Promise.reject(new Error('Quantity must be at least 1'));
                }
                if (qty > maxQty) {
                  return Promise.reject(new Error(`Quantity cannot exceed ${maxQty}`));
                }
                return Promise.resolve();
              },
            }),
          ]}
        >
            <InputNumber min={1} max={product?.quantity || 1} style={{ width: '100%' }} />
          </Form.Item>

          {/* Batch Number - only shown when physicalReceived is Yes */}
          {!isPhysicalNo && (
            <Form.Item name="batchNumber" label="Batch Number" rules={[{ required: true, message: 'Please enter the batch number!' }]}>
              <Input placeholder="Enter batch number" />
            </Form.Item>
          )}

          {/* Expiry Date - only shown when physicalReceived is Yes */}
          {!isPhysicalNo && (
            <Form.Item name="expiryDate" label="Expiry Date">
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          )}

          {basketType === 'good' && (
            <>
              <Form.Item name="packagingCondition" label="Packaging Condition" rules={[{ required: true, message: 'Please select condition!' }]}>
                <Select options={PACKAGING_CONDITION_OPTIONS} />
              </Form.Item>
              <Form.Item name="locationToStore" label="Location To Store" rules={[{ required: true, message: 'Please select location!' }]}>
                <Select options={LOCATION_TO_STORE_OPTIONS} />
              </Form.Item>
              <div className="inspection-form-full">
                <Form.Item name="remarks" label="Remarks">
                  <TextArea rows={2} />
                </Form.Item>
              </div>
            </>
          )}

          {basketType === 'repacking' && (
            <>
              <Form.Item name="packageType" label="Package Type" rules={[{ required: true, message: 'Please select package type!' }]}>
                <Select options={PACKAGE_TYPE_OPTIONS} />
              </Form.Item>
              <div className="inspection-form-full">
                <Form.Item name="needLabelChange" valuePropName="checked">
                  <Checkbox>Need Label Change</Checkbox>
                </Form.Item>
                <Form.Item name="needBarcodeChange" valuePropName="checked">
                  <Checkbox>Need Barcode Change</Checkbox>
                </Form.Item>
              </div>
              <div className="inspection-form-full">
                <Form.Item name="repackingNotes" label="Repacking Notes">
                  <TextArea rows={2} />
                </Form.Item>
              </div>
            </>
          )}

          {basketType === 'damaged' && (
            <>
              <Form.Item name="damageType" label="Damage Type" rules={[{ required: true, message: 'Please select damage type!' }]}>
                <Select options={DAMAGE_TYPE_OPTIONS} />
              </Form.Item>
              <Form.Item name="damageSeverity" label="Damage Severity" rules={[{ required: true, message: 'Please select severity!' }]}>
                <Select options={DAMAGE_SEVERITY_OPTIONS} />
              </Form.Item>
              <div className="inspection-form-full">
                <Form.Item name="damageNotes" label="Damage Notes">
                  <TextArea rows={2} />
                </Form.Item>
              </div>
            </>
          )}

          {basketType === 'scrap' && (
            <>
              {/* Scrap Reason - only shown when physicalReceived is Yes */}
              {!isPhysicalNo && (
                <Form.Item name="scrapReason" label="Scrap Reason" rules={[{ required: true, message: 'Please select a scrap reason!' }]}>
                  <Select options={SCRAP_REASON_OPTIONS} />
                </Form.Item>
              )}
              {/* Disposal Method - only shown when physicalReceived is Yes */}
              {!isPhysicalNo && (
                <Form.Item name="disposalMethod" label="Disposal Method" rules={[{ required: true, message: 'Please select a disposal method!' }]}>
                  <Select options={DISPOSAL_METHOD_OPTIONS} />
                </Form.Item>
              )}
              <div className="inspection-form-full">
                <Form.Item name="scrapNotes" label="Scrap Notes">
                  <TextArea rows={3} />
                </Form.Item>
              </div>
            </>
          )}
        </div>
      </Form>
    </Modal>
  );
};

export default InspectionFormModal;